import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Crown, Medal, Award, Trophy, Gem, Star, CheckCircle } from 'lucide-react';
import { getStoredUser, formatCurrency } from '../utils/helpers';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

const ConfirmSubscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const username = searchParams.get('username');
  const deposit = searchParams.get('deposit');
  const plane = searchParams.get('plane');
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const planIcons = {
    '1k': Crown,
    '1k5': Medal,
    '2k': Award,
    '2k5': Trophy,
    '3k': Gem,
    '3k5': Star
  };

  const planColors = {
    '1k': 'from-yellow-500 to-amber-600',
    '1k5': 'from-orange-500 to-red-600',
    '2k': 'from-blue-500 to-cyan-600',
    '2k5': 'from-purple-500 to-pink-600',
    '3k': 'from-emerald-500 to-teal-600',
    '3k5': 'from-rose-500 to-pink-600'
  };

  const planNames = {
    '1k': '1K Subscription Plan',
    '1k5': '1.5K Subscription Plan',
    '2k': '2K Subscription Plan',
    '2k5': '2.5K Subscription Plan',
    '3k': '3K Subscription Plan',
    '3k5': '3.5K Subscription Plan'
  };

  const planROI = {
    '1k': 5,
    '1k5': 7,
    '2k': 10,
    '2k5': 12,
    '3k': 15,
    '3k5': 18
  };

  useEffect(() => {
    const userData = getStoredUser();
    if (!userData || !userData.telegramName) {
      navigate('/login');
      return;
    }
    setUser(userData);

    // Verify username matches
    if (username && username !== userData.telegramName) {
      toast.error('Invalid subscription link');
      navigate('/investment');
    }
  }, [navigate, username]);

  const handleConfirm = async () => {
    if (!user || !plane || !deposit) {
      toast.error('Invalid subscription data');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/payment/SubscribePlan-web`, {
        telegramName: user.telegramName,
        planId: plane,
        amount: parseFloat(deposit)
      });

      if (response.status === 200) {
        setConfirmed(true);
        toast.success('🎉 Subscription confirmed successfully!');
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/investment');
        }, 3000);
      }
    } catch (error) {
      console.error('Error confirming subscription:', error);
      toast.error(error.response?.data?.error || 'Failed to confirm subscription');
    } finally {
      setLoading(false);
    }
  };

  const Icon = planIcons[plane] || Crown;
  const colorGradient = planColors[plane] || 'from-yellow-500 to-amber-600';
  const planName = planNames[plane] || 'Subscription Plan';
  const roi = planROI[plane] || 0;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Subscription Confirmed!</h1>
            <p className="text-lg text-slate-600 mb-8">
              Your {planName} has been activated successfully.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <p className="text-green-900 font-semibold">
                You'll start earning {roi}% ROI on your ${formatCurrency(parseFloat(deposit))} investment.
              </p>
            </div>
            <p className="text-sm text-slate-500 mt-6">
              Redirecting to PENE Investment...
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/investment')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Investment</span>
          </button>
        </div>

        {/* Subscription Plan Card */}
        <div className={`bg-gradient-to-r ${colorGradient} rounded-3xl shadow-2xl p-8 mb-8 text-white`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Icon size={40} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">{planName}</h1>
              <p className="text-white/80">Confirm Your Subscription</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/80 text-sm mb-1">Investment Amount</p>
              <p className="text-3xl font-bold">${formatCurrency(parseFloat(deposit))}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/80 text-sm mb-1">Expected ROI</p>
              <p className="text-3xl font-bold">{roi}%</p>
            </div>
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Subscription Details</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-slate-600">Account</span>
              <span className="font-semibold text-slate-900">@{username}</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-slate-600">Plan</span>
              <span className="font-semibold text-slate-900">{planName}</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-slate-600">Investment</span>
              <span className="font-semibold text-slate-900">${formatCurrency(parseFloat(deposit))}</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-slate-600">ROI</span>
              <span className="font-semibold text-slate-900">{roi}%</span>
            </div>
            
            <div className="flex items-center justify-between py-3">
              <span className="text-slate-600">Duration</span>
              <span className="font-semibold text-slate-900">30 days</span>
            </div>
          </div>
        </div>

        {/* Confirm Button */}
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="w-full py-4 bg-slate-900 text-white font-bold text-lg rounded-xl hover:bg-slate-800 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Confirming Subscription...' : 'Confirm Subscription'}
        </button>

        {/* Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-900">
            <strong>ℹ️ Note:</strong> By confirming, you agree to invest ${formatCurrency(parseFloat(deposit))} in the {planName}. 
            You will start earning {roi}% returns over the 30-day period.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ConfirmSubscription;
